import React from 'react';
import './Pages.css';

const AIDisclosure: React.FC = () => {
  return (
    <div className="page-container">
      <div className="page-header">
        <h1>AI Disclosure & Limitations</h1>
      </div>
      
      <div className="page-content legal-content">
        <p className="last-updated">Last updated: January 2024</p>

        <section className="content-section">
          <h2>Important Notice About AI-Generated Content</h2>
          <p>
            <strong>All portraits created by GET AI PORTRAIT are artificially generated using 
            machine learning technology.</strong> These images are artistic interpretations 
            created by AI algorithms and should be understood as creative works, not actual 
            photographs or accurate representations of reality.
          </p>
        </section>

        <section className="content-section">
          <h2>How Our AI Technology Works</h2>
          <p>
            GET AI PORTRAIT uses advanced machine learning models, including Stable Diffusion 
            and custom-trained neural networks, to transform your selfies into artistic portraits. 
            The process involves:
          </p>
          <ul>
            <li>Analyzing facial features from your uploaded photos</li>
            <li>Applying artistic style transformations</li>
            <li>Generating new images that maintain identity characteristics</li>
            <li>Enhancing and refining the output for quality</li>
          </ul>
          <p>
            <strong>Important:</strong> We use face detection (not face recognition) technology. 
            We do NOT create facial recognition databases or use your biometric data for 
            identification purposes.
          </p>
        </section>

        <section className="content-section">
          <h2>Limitations of AI-Generated Portraits</h2>
          <p>Please be aware of the following limitations:</p>
          <ul>
            <li><strong>Artistic Interpretation:</strong> Results are artistic renderings, not realistic photographs</li>
            <li><strong>Potential Inaccuracies:</strong> AI may introduce visual artifacts or alter features unintentionally</li>
            <li><strong>Not for Official Use:</strong> Generated portraits should NOT be used for:
              <ul>
                <li>Official identification documents</li>
                <li>Legal or government purposes</li>
                <li>Identity verification</li>
                <li>Medical or diagnostic purposes</li>
              </ul>
            </li>
            <li><strong>Style Variations:</strong> Different styles may produce varying levels of resemblance</li>
            <li><strong>Quality Dependencies:</strong> Results depend on input photo quality and clarity</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>AI Bias and Fairness</h2>
          <p>
            We acknowledge that AI systems can exhibit biases. We continuously work to:
          </p>
          <ul>
            <li>Train our models on diverse datasets</li>
            <li>Test across different demographics</li>
            <li>Improve representation and fairness</li>
            <li>Address reported issues promptly</li>
          </ul>
          <p>
            If you experience issues with portrait generation related to skin tone, ethnicity, 
            or other characteristics, please contact us at <a href="mailto:info@dreif-ai.com">info@dreif-ai.com</a>.
          </p>
        </section>

        <section className="content-section">
          <h2>Prohibited Uses</h2>
          <p>You must NOT use AI-generated portraits for:</p>
          <ul>
            <li>Creating deepfakes or misleading content</li>
            <li>Impersonating others without consent</li>
            <li>Harassment, defamation, or harmful purposes</li>
            <li>Violating others' privacy or publicity rights</li>
            <li>Any illegal or fraudulent activities</li>
            <li>Creating inappropriate or offensive content</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>EU AI Act Compliance</h2>
          <p>
            In compliance with the EU Artificial Intelligence Act (effective 2024), we ensure:
          </p>
          <ul>
            <li>All AI-generated content is clearly labeled as artificially created</li>
            <li>Transparency in our AI processing methods</li>
            <li>No use of prohibited AI practices</li>
            <li>Respect for fundamental rights and freedoms</li>
            <li>Implementation of appropriate technical and organizational measures</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Content Labeling</h2>
          <p>
            All portraits generated by our app include metadata indicating AI generation. 
            When sharing on social media or other platforms, we encourage users to:
          </p>
          <ul>
            <li>Disclose that images are AI-generated</li>
            <li>Use hashtags like #AIGenerated or #AIArt</li>
            <li>Not misrepresent AI portraits as real photographs</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Biometric Data Processing</h2>
          <p>
            <strong>Important Privacy Information:</strong>
          </p>
          <ul>
            <li>We process facial features solely for artistic transformation</li>
            <li>We do NOT store biometric templates after processing</li>
            <li>We do NOT create or maintain facial recognition databases</li>
            <li>We do NOT share biometric data with third parties</li>
            <li>Face detection data is deleted within 30 days of processing</li>
          </ul>
          <p>
            Your explicit consent is required for processing photos containing faces, 
            in accordance with GDPR Article 9 requirements for special category data.
          </p>
        </section>

        <section className="content-section">
          <h2>Accuracy Disclaimer</h2>
          <p>
            While we strive for high-quality results, GET AI PORTRAIT:
          </p>
          <ul>
            <li>Does not guarantee perfect likeness or accuracy</li>
            <li>May produce unexpected or unintended results</li>
            <li>Cannot be held liable for dissatisfaction with artistic interpretation</li>
            <li>Offers refunds within 7 days if technical issues prevent proper generation</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Questions or Concerns?</h2>
          <p>
            If you have questions about our AI technology, its limitations, or how we process 
            your data, please contact us:
          </p>
          <p>
            Email: <a href="mailto:info@dreif-ai.com">info@dreif-ai.com</a><br/>
            Data Protection Officer: <a href="mailto:dpo@dreif-ai.com">dpo@dreif-ai.com</a>
          </p>
        </section>
      </div>
    </div>
  );
};

export default AIDisclosure;